import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  useTheme,
  useMediaQuery,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as TemplateIcon,
  Category as CategoryIcon,
  Message as MessageIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import SmsEncodingNotice from './SmsEncodingNotice';
import { analyzeSmsEncoding } from '../utils/smsEncoding';

const TemplateManager = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  const [templateData, setTemplateData] = useState({
    name: '',
    category: '',
    message_text: '',
    variables: '',
    is_active: true
  });
  const smsAnalysis = analyzeSmsEncoding(templateData.message_text);

  // Load templates on component mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sms/templates`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        setError('Failed to load templates');
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setTemplateData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Auto-detect variables when message text changes
      if (field === 'message_text') {
        const variables = detectVariables(value);
        newData.variables = variables.join(', ');
      }
      
      return newData;
    });
  };

  // Function to detect variables in message text
  const detectVariables = (messageText) => {
    // Handle both {{variable}} and {variable} formats
    const variableRegex = /\{\{?(\w+)\}\}?/g;
    const variables = [];
    let match;
    
    while ((match = variableRegex.exec(messageText)) !== null) {
      if (!variables.includes(match[1])) {
        variables.push(match[1]);
      }
    }
    
    return variables;
  };

  // Function to generate preview with sample data
  const generatePreview = (messageText) => {
    const sampleData = {
      name: 'Temitope Akinyera',
      firstName: 'Temitope',
      lastName: 'Akinyera',
      phoneNumber: '+1234567890',
      email: 'temitope@example.com',
      city: 'Toronto',
      serviceDate: '2025-01-15',
      visitDate: '2025-01-15',
      conversionDate: '2025-01-15'
    };
    
    let preview = messageText;
    Object.keys(sampleData).forEach(key => {
      // Handle both {{variable}} and {variable} formats
      const doubleBraces = `{{${key}}}`;
      const singleBraces = `{${key}}`;
      const value = sampleData[key];
      
      preview = preview.replace(new RegExp(doubleBraces, 'g'), value);
      preview = preview.replace(new RegExp(singleBraces, 'g'), value);
    });
    
    return preview;
  };

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      // Convert variables object to comma-separated string for display
      let variablesString = '';
      if (template.variables && typeof template.variables === 'object') {
        if (Array.isArray(template.variables)) {
          variablesString = template.variables.join(', ');
        } else {
          variablesString = Object.keys(template.variables).join(', ');
        }
      } else if (typeof template.variables === 'string') {
        variablesString = template.variables;
      }
      
      setTemplateData({
        name: template.name,
        category: template.category,
        message_text: template.message_text,
        variables: variablesString,
        is_active: template.is_active
      });
    } else {
      setEditingTemplate(null);
      setTemplateData({
        name: '',
        category: '',
        message_text: '',
        variables: '',
        is_active: true
      });
    }
    setShowDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingTemplate(null);
    setTemplateData({
      name: '',
      category: '',
      message_text: '',
      variables: '',
      is_active: true
    });
  };

  const handleSaveTemplate = async () => {
    try {
      if (!templateData.name.trim() || !templateData.message_text.trim()) {
        setError('Name and message text are required');
        return;
      }

      if (templateData.message_text.length > 320) {
        setError('Message cannot exceed 320 characters (2 SMS pages)');
        return;
      }

      // Convert variables string to object for backend
      let variablesObj = {};
      if (templateData.variables && templateData.variables.trim()) {
        const variablesList = templateData.variables.split(',').map(v => v.trim()).filter(v => v);
        variablesList.forEach(variable => {
          variablesObj[variable] = variable;
        });
      }

      const url = editingTemplate 
        ? `${process.env.REACT_APP_API_URL}/api/sms/templates/${editingTemplate.id}`
        : `${process.env.REACT_APP_API_URL}/api/sms/templates`;
      
      const method = editingTemplate ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...templateData,
          variables: variablesObj
        })
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(editingTemplate ? 'Template updated successfully!' : 'Template created successfully!');
        handleCloseDialog();
        loadTemplates();
      } else {
        setError(result.error || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      setError('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/sms/templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setSuccess('Template deleted successfully!');
        loadTemplates();
      } else {
        const result = await response.json();
        setError(result.error || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Failed to delete template');
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'welcome': 'success',
      'follow_up': 'primary',
      'reminder': 'warning',
      'announcement': 'info',
      'general': 'default'
    };
    return colors[category] || 'default';
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusText = (isActive) => {
    return isActive ? 'Active' : 'Inactive';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ color: '#DC2626', fontWeight: 600 }}>
          <TemplateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          SMS Templates
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)'
            }
          }}
        >
          New Template
        </Button>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Templates Table */}
      {isMobile ? (
        // Mobile Cards View
        <Grid container spacing={2}>
          {templates
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((template) => (
              <Grid item xs={12} key={template.id}>
                <Card sx={{ 
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#374151' }}>
                        {template.name}
                      </Typography>
                      <Chip 
                        label={getStatusText(template.is_active)}
                        color={getStatusColor(template.is_active)}
                        size="small"
                      />
                    </Box>
                    
                    <Chip 
                      label={template.category}
                      color={getCategoryColor(template.category)}
                      size="small"
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
                      {template.message_text}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary">
                      {template.message_text.length}/320 characters
                    </Typography>
                  </CardContent>
                  
                  <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                    <IconButton 
                      color="primary" 
                      onClick={() => handleOpenDialog(template)}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      color="error" 
                      onClick={() => handleDeleteTemplate(template.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
        </Grid>
      ) : (
        // Desktop Table View
        <TableContainer component={Paper} sx={{ background: '#ffffff' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ background: '#f9fafb' }}>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((template) => (
                  <TableRow key={template.id} sx={{ '&:hover': { background: '#f9fafb' } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{template.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={template.category}
                        color={getCategoryColor(template.category)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {template.message_text}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {template.message_text.length}/320
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusText(template.is_active)}
                        color={getStatusColor(template.is_active)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        color="primary" 
                        onClick={() => handleOpenDialog(template)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteTemplate(template.id)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={templates.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Template Dialog */}
      <Dialog 
        open={showDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingTemplate ? 'Edit Template' : 'Create New Template'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Template Name"
                value={templateData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Welcome Message"
                sx={{ mb: 2 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={templateData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="welcome">Welcome</MenuItem>
                  <MenuItem value="follow_up">Follow Up</MenuItem>
                  <MenuItem value="reminder">Reminder</MenuItem>
                  <MenuItem value="announcement">Announcement</MenuItem>
                  <MenuItem value="general">General</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Message Text"
                value={templateData.message_text}
                onChange={(e) => handleInputChange('message_text', e.target.value)}
                placeholder="Type your message template here... Use {{name}} for variables"
                helperText={`${templateData.message_text.length}/320 characters`}
                error={templateData.message_text.length > 320}
                sx={{ mb: 2 }}
              />
              <SmsEncodingNotice message={templateData.message_text} />
              
              {/* Live Preview Section */}
              {templateData.message_text && (
                <Box sx={{ 
                  p: 2, 
                  bgcolor: '#f0f9ff', 
                  borderRadius: 1, 
                  border: '1px solid #0ea5e9',
                  mb: 2 
                }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#0c4a6e' }}>
                    🔍 Live Preview (with sample data):
                  </Typography>
                  <Paper sx={{ p: 2, background: '#ffffff', border: '1px solid #0ea5e9' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {generatePreview(templateData.message_text)}
                    </Typography>
                  </Paper>
                  <Typography variant="caption" sx={{ color: '#0369a1', mt: 1, display: 'block' }}>
                    💡 This shows how your message will look when sent with actual recipient data
                  </Typography>
                </Box>
              )}
              
              {/* Variable Help Section */}
              <Box sx={{ 
                p: 2, 
                bgcolor: '#f8fafc', 
                borderRadius: 1, 
                border: '1px solid #e2e8f0',
                mb: 2 
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#374151' }}>
                  📝 Available Variables You Can Use:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}>
                      <strong>Personal Info:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                      • {'{name}'} - Full name (e.g., "John Smith")<br/>
                      • {'{firstName}'} - First name only<br/>
                      • {'{lastName}'} - Last name only<br/>
                      • {'{phoneNumber}'} - Phone number<br/>
                      • {'{email}'} - Email address<br/>
                      • {'{city}'} - City name
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#64748b' }}>
                      <strong>Dates & Events:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', fontFamily: 'monospace' }}>
                      • {'{serviceDate}'} - Date they visited<br/>
                      • {'{visitDate}'} - Same as serviceDate<br/>
                      • {'{conversionDate}'} - When they accepted Christ
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="caption" sx={{ color: '#94a3b8', mt: 1, display: 'block' }}>
                  💡 Example: "Hello {'{firstName}'}, thank you for visiting us on {'{visitDate}'}!"
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Variables (Optional)"
                value={templateData.variables}
                onChange={(e) => handleInputChange('variables', e.target.value)}
                placeholder="e.g., name, date, event (comma separated)"
                helperText="Variables that can be replaced in the message"
                sx={{ mb: 2 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={templateData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.value)}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTemplate} 
            disabled={smsAnalysis.hasEmoji}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)'
              }
            }}
          >
            {editingTemplate ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TemplateManager;
